import type { UTMParams } from '../types';

const UTM_COOKIE_NAME = '__uvilo_utm';
const UTM_COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 30;

function toCookieValue(utm: UTMParams): string {
  return encodeURIComponent(JSON.stringify(utm));
}

function fromCookieValue(value: string): UTMParams | null {
  try {
    const parsed = JSON.parse(decodeURIComponent(value)) as Record<string, unknown>;
    return {
      utmSource: typeof parsed.utmSource === 'string' ? parsed.utmSource : undefined,
      utmMedium: typeof parsed.utmMedium === 'string' ? parsed.utmMedium : undefined,
      utmCampaign: typeof parsed.utmCampaign === 'string' ? parsed.utmCampaign : undefined,
      utmContent: typeof parsed.utmContent === 'string' ? parsed.utmContent : undefined,
      utmTerm: typeof parsed.utmTerm === 'string' ? parsed.utmTerm : undefined,
    };
  } catch {
    return null;
  }
}

function readUtmCookie(): UTMParams | null {
  if (typeof document === 'undefined') {
    return null;
  }

  const cookie = document.cookie
    .split(';')
    .map((part) => part.trim())
    .find((part) => part.startsWith(`${UTM_COOKIE_NAME}=`));

  if (!cookie) {
    return null;
  }

  const [, value = ''] = cookie.split('=');
  return fromCookieValue(value);
}

function writeUtmCookie(utm: UTMParams): void {
  if (typeof document === 'undefined') {
    return;
  }

  document.cookie = `${UTM_COOKIE_NAME}=${toCookieValue(utm)}; path=/; max-age=${UTM_COOKIE_MAX_AGE_SECONDS}; SameSite=Lax`;
}

function hasAnyUtmValue(utm: UTMParams | null): boolean {
  if (!utm) {
    return false;
  }

  return Boolean(
    utm.utmSource || utm.utmMedium || utm.utmCampaign || utm.utmContent || utm.utmTerm,
  );
}

function readUtmFromLocation(): UTMParams {
  if (typeof window === 'undefined') {
    return {};
  }

  const params = new URLSearchParams(window.location.search);
  return {
    utmSource: params.get('utm_source') ?? undefined,
    utmMedium: params.get('utm_medium') ?? undefined,
    utmCampaign: params.get('utm_campaign') ?? undefined,
    utmContent: params.get('utm_content') ?? undefined,
    utmTerm: params.get('utm_term') ?? undefined,
  };
}

export function captureFirstTouchUtm(): UTMParams {
  const existing = readUtmCookie();
  if (hasAnyUtmValue(existing)) {
    return existing ?? {};
  }

  const fromLocation = readUtmFromLocation();
  if (hasAnyUtmValue(fromLocation)) {
    writeUtmCookie(fromLocation);
    return fromLocation;
  }

  return {};
}

export function getStoredUtmParams(): UTMParams {
  return readUtmCookie() ?? {};
}
